import { useState, useEffect } from "react";
import { getDevices, getClients, getRmmStatus, getDetectedUsers, detectDeviceUsers, updateDevice, getUserSuggestions } from "../api";
import { Monitor, Download, ChevronDown, ChevronRight, Wifi, WifiOff,
         Activity, User, Building2, RefreshCw, Package, UserCheck, Search, Smartphone, CornerDownRight, UserPlus, Check } from "lucide-react";
import { tokens, FullPageSpinner } from "../theme";
import useIsMobile from "../hooks/useIsMobile";
import MacAgentInstall from "../components/MacAgentInstall";

const API_BASE = process.env.REACT_APP_API_URL || "";

// A device belongs to a contact (customer) OR an agent (staff). Used to group a
// user's phone with their computer.
function ownerKey(d) {
  return d.contact_id ? `c:${d.contact_id}` : d.agent_id ? `a:${d.agent_id}` : null;
}
function isMobileType(d) {
  return String(d.device_type).toLowerCase() === "mobile" || /android|ios/i.test(d.os || "");
}

// Nested mobile-device row, rendered indented directly beneath the owner's
// computer row so a user's phone shows up attached to their machine.
function MobileSubRow({ d, isMobile }) {
  const status = agentStatus(d.last_seen);
  const name = d.device_name || d.computer_name || "Mobile device";
  const go = () => { window.location.href = `/devices/${encodeURIComponent(d.id)}`; };

  if (isMobile) return (
    <div onClick={go} style={{ padding: "8px 16px 8px 30px", borderBottom: `1px solid ${tokens.border.soft}`, cursor: "pointer", background: "rgba(79,111,255,0.04)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
        <CornerDownRight size={12} color={tokens.text.faint} style={{ flexShrink: 0 }} />
        <Smartphone size={12} color={status.color} style={{ flexShrink: 0 }} />
        <span style={{ fontSize: 12, fontWeight: 600, color: tokens.text.primary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }}>{name}</span>
        {d.status && <span style={{ fontSize: 10, fontWeight: 600, padding: "1px 7px", borderRadius: 99, background: "rgba(79,111,255,0.12)", color: "#818cf8", flexShrink: 0 }}>{d.status}</span>}
      </div>
      <div style={{ fontSize: 10, color: tokens.text.faint, marginLeft: 31, marginTop: 2 }}>
        {[d.os && `${d.os}${d.os_version ? " " + d.os_version : ""}`, status.label, d.last_seen ? timeSince(d.last_seen) : "Never"].filter(Boolean).join(" · ")}
      </div>
    </div>
  );

  return (
    <div onClick={go} style={{
      display: "grid", gridTemplateColumns: "2fr 1.5fr 1fr 1.5fr 1fr 1fr", padding: "8px 16px", gap: 12, alignItems: "center",
      borderBottom: `1px solid ${tokens.border.soft}`, cursor: "pointer", background: "rgba(79,111,255,0.035)", transition: "background 0.15s",
    }}
      onMouseEnter={e => { e.currentTarget.style.background = "rgba(99,102,241,0.08)"; }}
      onMouseLeave={e => { e.currentTarget.style.background = "rgba(79,111,255,0.035)"; }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, paddingLeft: 16, minWidth: 0 }}>
        <CornerDownRight size={12} color={tokens.text.faint} style={{ flexShrink: 0 }} />
        <Smartphone size={13} color={status.color} style={{ flexShrink: 0 }} />
        <span style={{ fontSize: 11.5, fontWeight: 600, color: tokens.text.primary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</span>
      </div>
      <div style={{ fontSize: 11, color: tokens.text.secondary, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {[d.os, d.os_version].filter(Boolean).join(" ") || "—"}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
        <Activity size={10} color={status.color} /><span style={{ fontSize: 11, color: status.color, fontWeight: 600 }}>{status.label}</span>
      </div>
      <div style={{ fontSize: 10, color: tokens.text.deep, fontStyle: "italic" }}>same user</div>
      <div style={{ fontSize: 11, color: tokens.text.faint }}>{d.last_seen ? timeSince(d.last_seen) : "Never"}</div>
      <div>{d.status && <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 99, background: "rgba(79,111,255,0.12)", color: "#818cf8" }}>{d.status}</span>}</div>
    </div>
  );
}

function timeSince(dateStr) {
  if (!dateStr) return "Never";
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60)    return `${diff}s ago`;
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// Same online definition as the backend status cron + Devices page (5 min —
// agents check in every ~60s, backend marks offline at 5 min of silence).
function agentStatus(lastSeen) {
  if (!lastSeen) return { label: "No agent", color: "#475569" };
  const age = Date.now() - new Date(lastSeen).getTime();
  if (age < 5 * 60_000)     return { label: "Online",  color: "#22c55e" };
  if (age < 48 * 3600_000)  return { label: "Stale",   color: "#f59e0b" };
  return { label: "Offline", color: "#ef4444" };
}

// ── Download Card ─────────────────────────────────────────────
function DownloadCard({ label, icon, href, desc, badge }) {
  return (
    <a
      href={href}
      download
      style={{
        display: "flex", alignItems: "center", gap: 14,
        padding: "14px 16px",
        background: tokens.surface.panel,
        border: `1px solid ${tokens.border.soft}`,
        borderRadius: 10,
        textDecoration: "none",
        transition: "all 0.18s",
        cursor: "pointer",
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(99,102,241,0.4)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = tokens.border.soft; e.currentTarget.style.transform = "translateY(0)"; }}
    >
      <div style={{
        width: 42, height: 42, borderRadius: 9, flexShrink: 0,
        background: "rgba(99,102,241,0.12)", border: "1px solid rgba(99,102,241,0.25)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: tokens.text.primary }}>{label}</div>
        <div style={{ fontSize: 11, color: tokens.text.faint, marginTop: 2 }}>{desc}</div>
      </div>
      {badge && (
        <span style={{
          fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 99,
          background: "rgba(34,197,94,0.15)", color: "#22c55e",
          border: "1px solid rgba(34,197,94,0.3)", flexShrink: 0,
        }}>{badge}</span>
      )}
      <Download size={15} color={tokens.text.faint} style={{ flexShrink: 0 }} />
    </a>
  );
}

// ── Company Group ─────────────────────────────────────────────
// Suggested-user assign dropdown for an Unassigned device row. Fetches contacts
// whose name resembles the device's detected user / device name (server-ranked),
// and lets an admin pick one — or type to search all users. Assigning stamps the
// contact + their client via updateDevice (same path DetectedSignIns uses).
function AssignUserDropdown({ device, onAssigned }) {
  const [open, setOpen]       = useState(false);
  const [items, setItems]     = useState([]);
  const [q, setQ]             = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy]       = useState(false);
  const stop = e => e.stopPropagation();

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      getUserSuggestions(device.id, q.trim() || undefined)
        .then(r => { if (!cancelled) setItems(Array.isArray(r.data) ? r.data : []); })
        .catch(() => { if (!cancelled) setItems([]); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, q ? 250 : 0);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, q, device.id]);

  const assign = async (contactId) => {
    setBusy(true);
    try { await updateDevice(device.id, { contact_id: contactId }); setOpen(false); onAssigned?.(); }
    catch (e) { alert(e.response?.data?.error || "Failed to assign"); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ position: "relative", marginTop: 4 }} onClick={stop}>
      <button
        onClick={e => { stop(e); setOpen(o => !o); }}
        style={{
          display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 8px",
          background: "rgba(99,102,241,0.10)", border: "1px solid rgba(99,102,241,0.30)",
          borderRadius: 6, color: "#818cf8", fontSize: 10.5, fontWeight: 600, cursor: "pointer",
        }}
      >
        <UserPlus size={11} /> Assign user <ChevronDown size={10} />
      </button>
      {open && (
        <>
          {/* click-away backdrop */}
          <div onClick={e => { stop(e); setOpen(false); }} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
          <div style={{
            position: "absolute", zIndex: 41, top: "calc(100% + 4px)", left: 0, width: 290,
            background: tokens.surface.panel, border: `1px solid ${tokens.border.soft}`,
            borderRadius: 10, boxShadow: "0 16px 40px rgba(0,0,0,0.45)", padding: 8,
          }}>
            <input
              autoFocus value={q} onChange={e => setQ(e.target.value)} onClick={stop}
              placeholder="Search all users…"
              style={{
                width: "100%", boxSizing: "border-box", padding: "7px 10px",
                background: "rgba(0,0,0,0.25)", border: `1px solid ${tokens.border.soft}`,
                borderRadius: 7, color: tokens.text.primary, fontSize: 12, outline: "none",
              }}
            />
            <div style={{ maxHeight: 240, overflowY: "auto", marginTop: 6 }}>
              {loading ? (
                <div style={{ padding: "10px 8px", fontSize: 12, color: tokens.text.faint }}>Loading…</div>
              ) : items.length === 0 ? (
                <div style={{ padding: "10px 8px", fontSize: 12, color: tokens.text.faint }}>
                  {q ? "No matching users" : "No name-based suggestions — type to search"}
                </div>
              ) : (
                items.map(s => (
                  <button
                    key={s.contact_id} disabled={busy}
                    onClick={e => { stop(e); assign(s.contact_id); }}
                    style={{
                      display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1,
                      width: "100%", textAlign: "left", padding: "7px 9px", marginBottom: 2,
                      background: "none", border: "none", borderRadius: 7,
                      cursor: busy ? "default" : "pointer",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = "rgba(99,102,241,0.10)"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "none"; }}
                  >
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: tokens.text.primary, display: "flex", alignItems: "center", gap: 6 }}>
                      <Check size={11} color="#22c55e" style={{ opacity: 0.75 }} /> {s.name}
                    </span>
                    <span style={{ fontSize: 10.5, color: tokens.text.faint, paddingLeft: 17 }}>
                      {[s.company, s.email].filter(Boolean).join(" · ")}
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function CompanyGroup({ companyName, devices, mobilesByOwner = {}, onAssigned }) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(true);
  // Track which owners' phones we've already nested, so a phone attaches under
  // only the first computer of a user who has several.
  const nestedShown = new Set();

  const onlineCount = devices.filter(d => {
    if (!d.last_seen) return false;
    return Date.now() - new Date(d.last_seen).getTime() < 5 * 60_000;
  }).length;

  return (
    <div style={{
      background: tokens.surface.panel,
      border: `1px solid ${tokens.border.soft}`,
      borderRadius: 10,
      overflow: "hidden",
      marginBottom: 10,
    }}>
      {/* Company header */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: 10,
          padding: "12px 16px",
          background: "none", border: "none", cursor: "pointer",
          borderBottom: open ? `1px solid ${tokens.border.soft}` : "none",
          transition: "background 0.15s",
        }}
        onMouseEnter={e => { e.currentTarget.style.background = "rgba(99,102,241,0.05)"; }}
        onMouseLeave={e => { e.currentTarget.style.background = "none"; }}
      >
        <Building2 size={15} color="#818cf8" style={{ flexShrink: 0 }} />
        <span style={{ fontSize: 14, fontWeight: 700, color: tokens.text.primary, flex: 1, textAlign: "left" }}>
          {companyName}
        </span>
        <span style={{ fontSize: 11, color: tokens.text.faint }}>
          {devices.length} device{devices.length !== 1 ? "s" : ""}
        </span>
        {onlineCount > 0 && (
          <span style={{
            fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 99,
            background: "rgba(34,197,94,0.12)", color: "#22c55e",
            border: "1px solid rgba(34,197,94,0.25)", marginLeft: 6,
          }}>
            {onlineCount} online
          </span>
        )}
        {open ? <ChevronDown size={14} color={tokens.text.faint} /> : <ChevronRight size={14} color={tokens.text.faint} />}
      </button>

      {/* Device rows */}
      {open && (
        <div>
          {/* Table header — desktop only */}
          {!isMobile && (
            <div style={{
              display: "grid",
              gridTemplateColumns: "2fr 1.5fr 1fr 1.5fr 1fr 1fr",
              padding: "7px 16px",
              background: "rgba(0,0,0,0.2)",
              fontSize: 10, fontWeight: 700, color: tokens.text.faint,
              textTransform: "uppercase", letterSpacing: "0.05em",
              gap: 12,
            }}>
              <span>Device</span>
              <span>OS / IP</span>
              <span>Agent</span>
              <span>Assigned User</span>
              <span>Last Seen</span>
              <span>Status</span>
            </div>
          )}

          {devices.map((d, i) => {
            const name   = d.device_name || d.computer_name || "Unknown";
            const status = agentStatus(d.last_seen);
            const isLast = i === devices.length - 1;
            const isMobileDev = isMobileType(d);
            const RowIcon = isMobileDev ? Smartphone : Monitor;
            const dKey = ownerKey(d);
            // Phones owned by this row's user, to nest directly beneath it.
            let nested = [];
            if (!isMobileDev && dKey && mobilesByOwner[dKey] && !nestedShown.has(dKey)) {
              nested = mobilesByOwner[dKey];
              nestedShown.add(dKey);
            }
            const nestedRows = nested.map(m => <MobileSubRow key={m.id} d={m} isMobile={isMobile} />);
            const badgeBg = d.status === "healthy" || d.status === "online" ? "rgba(16,185,129,0.12)" :
                            d.status === "warning"  ? "rgba(245,158,11,0.12)" :
                            d.status === "critical" ? "rgba(239,68,68,0.12)" : "rgba(71,85,105,0.15)";
            const badgeColor = d.status === "healthy" || d.status === "online" ? "#10b981" :
                               d.status === "warning"  ? "#f59e0b" :
                               d.status === "critical" ? "#ef4444" : "#475569";

            // Mobile — stacked card: name + status badge, then wrapping meta line
            if (isMobile) return (
              <div key={d.id}>
              <div
                onClick={() => { window.location.href = `/devices/${encodeURIComponent(d.id)}`; }}
                style={{
                  padding: "12px 16px",
                  borderBottom: !isLast ? `1px solid ${tokens.border.soft}` : "none",
                  cursor: "pointer",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                  <RowIcon size={13} color={status.color} style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: tokens.text.primary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: "monospace", flex: 1, minWidth: 0 }}>
                    {name}
                  </span>
                  {d.status && (
                    <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 99, background: badgeBg, color: badgeColor, flexShrink: 0 }}>
                      {d.status}
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "4px 12px", marginTop: 6 }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                    <Activity size={10} color={status.color} />
                    <span style={{ fontSize: 11, color: status.color, fontWeight: 600 }}>{status.label}</span>
                    {d.agent_version && <span style={{ fontSize: 10, color: tokens.text.faint, fontFamily: "monospace" }}>v{d.agent_version}</span>}
                  </span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                    {status.label === "Online" ? <Wifi size={10} color="#22c55e" /> : <WifiOff size={10} color={tokens.text.faint} />}
                    <span style={{ fontSize: 11, color: tokens.text.faint }}>{d.last_seen ? timeSince(d.last_seen) : "Never"}</span>
                  </span>
                  {(d.contact_name || d.last_user) && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                      <User size={10} color={d.contact_name ? "#818cf8" : tokens.text.faint} />
                      <span style={{ fontSize: 11, color: d.contact_name ? "#818cf8" : tokens.text.faint }}>{d.contact_name || d.last_user}</span>
                    </span>
                  )}
                  {(d.os || d.ip_address) && (
                    <span style={{ fontSize: 11, color: tokens.text.faint }}>
                      {[d.os, d.ip_address].filter(Boolean).join(" · ")}
                    </span>
                  )}
                </div>
              </div>
              {nestedRows}
              </div>
            );

            return (
              <div key={d.id}>
              <div
                onClick={() => { window.location.href = `/devices/${encodeURIComponent(d.id)}`; }}
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1.5fr 1fr 1.5fr 1fr 1fr",
                  padding: "11px 16px",
                  gap: 12,
                  alignItems: "center",
                  borderBottom: (!isLast || nestedRows.length) ? `1px solid ${tokens.border.soft}` : "none",
                  cursor: "pointer",
                  transition: "background 0.15s",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(99,102,241,0.04)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "none"; }}
              >
                {/* Device name */}
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                    <RowIcon size={13} color={status.color} style={{ flexShrink: 0 }} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: tokens.text.primary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: "monospace" }}>
                      {name}
                    </span>
                  </div>
                </div>

                {/* OS / IP */}
                <div style={{ minWidth: 0 }}>
                  {d.os && <div style={{ fontSize: 11, color: tokens.text.secondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.os}</div>}
                  {d.ip_address && <div style={{ fontSize: 10, color: tokens.text.faint, fontFamily: "monospace" }}>{d.ip_address}</div>}
                  {!d.os && !d.ip_address && <span style={{ fontSize: 11, color: tokens.text.deep }}>—</span>}
                </div>

                {/* Agent status + version */}
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <Activity size={10} color={status.color} />
                    <span style={{ fontSize: 11, color: status.color, fontWeight: 600 }}>{status.label}</span>
                  </div>
                  <div style={{ fontSize: 10, color: tokens.text.faint, marginTop: 1, fontFamily: "monospace" }}>
                    {d.agent_version ? `v${d.agent_version}` : status.label === "No agent" ? "—" : "v?"}
                  </div>
                </div>

                {/* Assigned user */}
                <div style={{ minWidth: 0 }}>
                  {d.contact_name ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                      <User size={10} color="#818cf8" />
                      <span style={{ fontSize: 11, color: "#818cf8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.contact_name}</span>
                    </div>
                  ) : d.last_user ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                      <User size={10} color={tokens.text.faint} />
                      <span style={{ fontSize: 11, color: tokens.text.faint, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.last_user}</span>
                    </div>
                  ) : (
                    <span style={{ fontSize: 11, color: tokens.text.deep }}>Unassigned</span>
                  )}
                  {d.agent_name && (
                    <div style={{ fontSize: 10, color: "var(--badge-violet)", marginTop: 1 }}>🧑‍💻 {d.agent_name}</div>
                  )}
                  {!d.contact_id && !isMobileType(d) && (
                    <AssignUserDropdown device={d} onAssigned={onAssigned} />
                  )}
                </div>

                {/* Last seen */}
                <div>
                  {d.last_seen ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      {status.label === "Online" ? <Wifi size={10} color="#22c55e" /> : <WifiOff size={10} color={tokens.text.faint} />}
                      <span style={{ fontSize: 11, color: tokens.text.faint }}>{timeSince(d.last_seen)}</span>
                    </div>
                  ) : (
                    <span style={{ fontSize: 11, color: tokens.text.deep }}>Never</span>
                  )}
                </div>

                {/* Device status badge */}
                <div>
                  {d.status && (
                    <span style={{
                      fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 99,
                      background: d.status === "healthy" || d.status === "online" ? "rgba(16,185,129,0.12)" :
                                  d.status === "warning"  ? "rgba(245,158,11,0.12)" :
                                  d.status === "critical" ? "rgba(239,68,68,0.12)" : "rgba(71,85,105,0.15)",
                      color: d.status === "healthy" || d.status === "online" ? "#10b981" :
                             d.status === "warning"  ? "#f59e0b" :
                             d.status === "critical" ? "#ef4444" : "#475569",
                    }}>
                      {d.status}
                    </span>
                  )}
                </div>
              </div>
              {nestedRows}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── RMM Health Summary ────────────────────────────────────────
function RmmHealthBanner({ status }) {
  if (!status) return null;
  const { commands = {}, devices: dv = {} } = status;
  const hasIssues = (commands.stuck || 0) > 0 || (commands.failed_24h || 0) > 0;

  return (
    <div style={{
      display: "flex", gap: 10, flexWrap: "wrap",
      padding: "10px 14px",
      background: "rgba(0,0,0,0.2)",
      borderRadius: 8, marginBottom: 16,
      border: `1px solid ${hasIssues ? "rgba(239,68,68,0.3)" : tokens.border.soft}`,
    }}>
      {[
        { label: "Active (1h)",   val: dv.active_1h  ?? "—", color: "#22c55e" },
        { label: "Active (24h)",  val: dv.active_24h ?? "—", color: "#818cf8" },
        { label: "Total devices", val: dv.total      ?? "—", color: tokens.text.primary },
        { label: "Commands (24h)",val: (commands.completed_24h ?? 0) + " ok", color: "#10b981" },
        { label: "Failed (24h)",  val: commands.failed_24h ?? 0, color: (commands.failed_24h || 0) > 0 ? "#ef4444" : tokens.text.faint },
        { label: "Stuck",         val: commands.stuck ?? 0, color: (commands.stuck || 0) > 0 ? "#f59e0b" : tokens.text.faint },
      ].map(({ label, val, color }) => (
        <div key={label} style={{ textAlign: "center", minWidth: 80 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color, lineHeight: 1 }}>{val}</div>
          <div style={{ fontSize: 10, color: tokens.text.faint, marginTop: 3 }}>{label}</div>
        </div>
      ))}
    </div>
  );
}

// ── Detected sign-ins review panel ────────────────────────────
// Shows who has signed into each device (from the who-signed-in probe). Exact
// email→contact matches auto-link server-side; this panel surfaces the rest for
// one-click assignment and lets admins re-run detection on demand.
function DetectedSignIns({ onAssigned }) {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [probing, setProbing] = useState(false);
  const [busy, setBusy]       = useState(null);

  const load = () => {
    getDetectedUsers().then(r => setRows(Array.isArray(r.data) ? r.data : [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const probe = async () => {
    setProbing(true);
    try { await detectDeviceUsers(); setTimeout(load, 4000); }
    catch (e) { /* ignore */ } finally { setTimeout(() => setProbing(false), 4000); }
  };
  const assign = async (row, contactId) => {
    const cid = contactId || row.match_contact_id;
    if (!cid) return;
    setBusy(row.id);
    try { await updateDevice(row.id, { contact_id: cid }); load(); onAssigned?.(); }
    catch (e) { alert(e.response?.data?.error || "Failed to assign"); }
    finally { setBusy(null); }
  };

  // Only show devices that are still unassigned (assigned ones drop off).
  const pending = rows.filter(r => !r.contact_id);
  if (loading) return null;

  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: tokens.text.faint, textTransform: "uppercase", letterSpacing: "0.06em", display: "flex", alignItems: "center", gap: 7 }}>
          <UserCheck size={13} /> Detected Sign-ins{pending.length ? ` · ${pending.length} unassigned` : ""}
        </div>
        <button onClick={probe} disabled={probing} style={{
          display: "flex", alignItems: "center", gap: 6, padding: "6px 12px",
          background: "none", border: `1px solid ${tokens.border.soft}`, borderRadius: 8,
          color: tokens.text.faint, fontSize: 12, cursor: probing ? "default" : "pointer", opacity: probing ? 0.6 : 1,
        }}>
          <Search size={12} /> {probing ? "Probing online devices…" : "Detect users now"}
        </button>
      </div>
      {pending.length === 0 ? (
        <div style={{ padding: "14px 16px", background: tokens.surface.panel, border: `1px solid ${tokens.border.soft}`, borderRadius: 10, fontSize: 12.5, color: tokens.text.faint }}>
          No unassigned devices with a detected sign-in. Probes run automatically every 15 min for online unassigned devices; exact email matches are auto-assigned.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {pending.map(r => (
            <div key={r.id} style={{
              display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
              padding: "11px 14px", background: tokens.surface.panel,
              border: `1px solid ${r.match_contact_id ? "rgba(34,197,94,0.3)" : tokens.border.soft}`, borderRadius: 10,
            }}>
              <Monitor size={15} color={tokens.text.faint} />
              <span style={{ fontSize: 13, fontWeight: 700, color: tokens.text.primary, minWidth: 150 }}>{r.device_name}</span>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontSize: 12.5, color: tokens.text.primary }}>
                  {r.detected_user || "(unknown)"}{r.detected_upn ? ` · ${r.detected_upn}` : ""}
                </div>
                <div style={{ fontSize: 11, color: tokens.text.faint, marginTop: 1 }}>
                  {r.detected_at ? `detected ${timeSince(r.detected_at)}` : ""}
                </div>
              </div>
              {r.match_contact_id ? (
                <button onClick={() => assign(r, r.match_contact_id)} disabled={busy === r.id} style={{
                  display: "flex", alignItems: "center", gap: 6, padding: "7px 13px",
                  background: "rgba(34,197,94,0.12)", border: "1px solid rgba(34,197,94,0.35)", borderRadius: 8,
                  color: "#22c55e", fontSize: 12, fontWeight: 600, cursor: busy === r.id ? "default" : "pointer",
                }} title="Email matched an M365 contact exactly">
                  <UserCheck size={13} /> Assign to {r.match_contact_name} ({r.match_company})
                </button>
              ) : r.suggest_contact_id ? (
                <button onClick={() => assign(r, r.suggest_contact_id)} disabled={busy === r.id} style={{
                  display: "flex", alignItems: "center", gap: 6, padding: "7px 13px",
                  background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.4)", borderRadius: 8,
                  color: "#f59e0b", fontSize: 12, fontWeight: 600, cursor: busy === r.id ? "default" : "pointer",
                }} title={`Name match (local account) — likely ${r.suggest_email}. Verify before assigning.`}>
                  <UserCheck size={13} /> Likely {r.suggest_contact_name} ({r.suggest_company})? Assign
                </button>
              ) : (
                <span style={{ fontSize: 11.5, color: tokens.text.faint, fontStyle: "italic" }}>
                  {r.detected_upn ? "No matching contact — assign manually below" : "Local account — no M365 email"}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────
export default function DeviceInventory() {
  const isMobile = useIsMobile();
  const [devices,    setDevices]    = useState([]);
  const [clients,    setClients]    = useState([]);
  const [rmmStatus,  setRmmStatus]  = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState("");
  const [filterClient, setFilterClient] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = async () => {
    try {
      const [devRes, clRes] = await Promise.all([getDevices({}), getClients()]);
      setDevices(Array.isArray(devRes.data) ? devRes.data : []);
      setClients(Array.isArray(clRes.data)  ? clRes.data  : []);
      setLastRefresh(new Date());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    getRmmStatus().then(r => setRmmStatus(r.data)).catch(() => {});
    const interval = setInterval(() => {
      load();
      getRmmStatus().then(r => setRmmStatus(r.data)).catch(() => {});
    }, 60_000);
    return () => clearInterval(interval);
  }, []);

  // Apply all three filters together — search, client, status.
  const visibleDevices = devices.filter(d => {
    if (filterClient && d.client_id !== filterClient) return false;
    if (filterStatus && (d.status || "unknown") !== filterStatus) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = [
        d.device_name, d.computer_name, d.contact_name, d.last_user, d.os, d.os_version,
        d.ip_address, d.client_name, d.manufacturer, d.model, d.hardware_id, d.serial_number,
      ];
      if (!hay.some(v => (v || "").toString().toLowerCase().includes(q))) return false;
    }
    return true;
  });

  // Status options derived from data so the dropdown only shows what exists.
  const statusCounts = devices.reduce((acc, d) => {
    const k = d.status || "unknown";
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
  const statusOptions = Object.entries(statusCounts).sort((a, b) => b[1] - a[1]).map(([k]) => k);

  // A user's phone nests under their computer. Build owner→phones across the
  // WHOLE visible set (phone and computer can sit in different client groups —
  // e.g. an agent-owned phone with no client), then keep phones out of the
  // top-level rows when their owner has a computer to nest under.
  const mobilesByOwner = {};
  for (const d of visibleDevices) {
    if (!isMobileType(d)) continue;
    const k = ownerKey(d);
    if (!k) continue;
    (mobilesByOwner[k] = mobilesByOwner[k] || []).push(d);
  }
  const ownersWithComputer = new Set(
    visibleDevices.filter(d => !isMobileType(d) && ownerKey(d)).map(d => ownerKey(d))
  );
  const topLevel = visibleDevices.filter(d =>
    !(isMobileType(d) && ownerKey(d) && ownersWithComputer.has(ownerKey(d)))
  );

  // Group by company (top-level rows only; nested phones render under their owner)
  const unassigned = topLevel.filter(d => !d.client_id);
  const byClient = {};
  for (const d of topLevel) {
    if (!d.client_id) continue;
    const key = d.client_id;
    if (!byClient[key]) byClient[key] = { name: d.client_name || "Unknown Company", devices: [] };
    byClient[key].devices.push(d);
  }

  // Sort companies by name
  const groups = Object.values(byClient).sort((a, b) => a.name.localeCompare(b.name));
  if (unassigned.length > 0) groups.push({ name: "Unassigned", devices: unassigned });

  // Totals reflect the filtered set; show "of N" so techs see the unfiltered universe too.
  const totalDevices  = devices.length;
  const visibleCount  = visibleDevices.length;
  // Same online definition as the backend status cron + Devices page (5 min).
  const onlineCount   = visibleDevices.filter(d => d.status === "online" || (d.last_seen && Date.now() - new Date(d.last_seen).getTime() < 5 * 60_000)).length;
  const isFiltered    = !!(search || filterClient || filterStatus);

  return (
    <div style={{ padding: isMobile ? 14 : 24, maxWidth: 1400 }}>

      {/* Page header */}
      <div style={{
        paddingBottom: 14, borderBottom: `1px solid ${tokens.border.soft}`,
        marginBottom: 20, display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 12,
      }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: tokens.text.primary, letterSpacing: "-0.5px" }}>Device Inventory</h1>
          <p style={{ color: tokens.text.faint, fontSize: 13, marginTop: 3 }}>
            {isFiltered ? `${visibleCount} of ${totalDevices}` : totalDevices} device{visibleCount !== 1 ? "s" : ""} across {groups.length} client{groups.length !== 1 ? "s" : ""}
            {" · "}{onlineCount} agent{onlineCount !== 1 ? "s" : ""} online
            {lastRefresh && ` · Refreshed ${lastRefresh.toLocaleTimeString()}`}
          </p>
        </div>
        <button
          onClick={() => { setLoading(true); load(); }}
          style={{
            display: "flex", alignItems: "center", gap: 6, padding: "8px 14px",
            background: "none", border: `1px solid ${tokens.border.soft}`, borderRadius: 8,
            color: tokens.text.faint, fontSize: 12, cursor: "pointer",
          }}
        >
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* RMM agent downloads */}
      <div style={{ marginBottom: 22 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: tokens.text.faint, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
          RMM Agent Downloads
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 10 }}>
          <DownloadCard
            label="MIDTC RMM Agent — Windows"
            icon={<Package size={20} color="#818cf8" />}
            href={`${API_BASE}/downloads/rmm-windows`}
            desc="Windows x64 · Self-contained EXE · .NET 8 Worker Service"
            badge="win-x64"
          />
          <DownloadCard
            label="MIDTC RMM Agent — macOS"
            icon={<Package size={20} color="#34d399" />}
            href={`${API_BASE}/downloads/rmm-mac`}
            desc="macOS arm64/x64 · signed, self-contained · no .NET needed"
            badge="darwin"
          />
          <a
            href={`${API_BASE}/downloads/rmm-agent`}
            style={{
              display: "flex", alignItems: "center", gap: 14,
              padding: "14px 16px",
              background: "rgba(99,102,241,0.06)",
              border: "1px solid rgba(99,102,241,0.25)",
              borderRadius: 10, textDecoration: "none",
              transition: "all 0.18s", cursor: "pointer",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(99,102,241,0.12)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(99,102,241,0.06)"; }}
          >
            <div style={{
              width: 42, height: 42, borderRadius: 9, flexShrink: 0,
              background: "rgba(99,102,241,0.15)", border: "1px solid rgba(99,102,241,0.3)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Download size={20} color="#818cf8" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#818cf8" }}>Auto-Detect Download</div>
              <div style={{ fontSize: 11, color: tokens.text.faint, marginTop: 2 }}>Detects your OS and redirects automatically</div>
            </div>
          </a>
        </div>

        {/* macOS enrolment. Kept inside the downloads block rather than as a
            separate section: the cards above hand over a binary, but a Mac is only
            actually enrolled (and only gets RustDesk) via the one-liner below, so
            the two belong together. */}
        <div style={{ marginTop: 10 }}>
          <MacAgentInstall
            palette={{
              panel:  tokens.surface.panel,
              border: tokens.border.soft,
              text:   tokens.text.primary,
              muted:  tokens.text.faint,
              faint:  tokens.text.faint,
              accent: "#818cf8",
            }}
          />
        </div>
      </div>

      {/* Detected sign-ins (who's on unassigned devices) */}
      <DetectedSignIns onAssigned={load} />

      {/* RMM health summary */}
      <RmmHealthBanner status={rmmStatus} />

      {/* Search + filters */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <input
          placeholder="🔍 Search devices, users, OS, IP, model..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            flex: isMobile ? "1 1 100%" : 1, minWidth: 200, padding: "9px 14px",
            background: tokens.surface.panel,
            border: `1px solid ${tokens.border.soft}`,
            borderRadius: 8, color: tokens.text.primary,
            fontSize: 13, boxSizing: "border-box",
          }}
        />
        <select
          value={filterClient}
          onChange={e => setFilterClient(e.target.value)}
          style={{
            padding: "9px 12px", background: tokens.surface.panel,
            border: `1px solid ${tokens.border.soft}`, borderRadius: 8,
            color: tokens.text.primary, fontSize: 13, cursor: "pointer",
          }}
        >
          <option value="">All Customers</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
        </select>
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          style={{
            padding: "9px 12px", background: tokens.surface.panel,
            border: `1px solid ${tokens.border.soft}`, borderRadius: 8,
            color: tokens.text.primary, fontSize: 13, cursor: "pointer",
          }}
        >
          <option value="">All Statuses ({totalDevices})</option>
          {statusOptions.map(s => <option key={s} value={s}>{s} ({statusCounts[s]})</option>)}
        </select>
        {isFiltered && (
          <button
            onClick={() => { setSearch(""); setFilterClient(""); setFilterStatus(""); }}
            style={{
              padding: "9px 14px", background: tokens.surface.panel,
              border: `1px solid ${tokens.border.soft}`, borderRadius: 8,
              color: tokens.text.faint, fontSize: 13, cursor: "pointer",
            }}
            title="Clear all filters"
          >Clear</button>
        )}
      </div>

      {/* Device groups */}
      {loading ? (
        <FullPageSpinner />
      ) : groups.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60, color: tokens.text.faint, fontSize: 14 }}>
          {search ? `No devices found matching "${search}"` : "No devices found."}
        </div>
      ) : (
        groups.map(g => (
          <CompanyGroup key={g.name} companyName={g.name} devices={g.devices} mobilesByOwner={mobilesByOwner} onAssigned={load} />
        ))
      )}

    </div>
  );
}
